import {
  Avatar,
  ColumnsContainer,
  Container,
  Divider,
  Button as EmailButton,
  getBorderStyles,
  Heading,
  Html,
  Image,
  Reader,
  renderToStaticMarkup,
  Spacer,
  Text,
  type TReaderDocument,
} from '@app-evolution-cms/email-builder';
import {
  closestCenter,
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DownloadIcon } from '@radix-ui/react-icons';
import compare from 'just-compare';
import {
  type ComponentProps,
  type ComponentType,
  Fragment,
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '../../../primitives/button';
import { Redo2Icon } from '../../../svg/redo-2-icon';
import { Undo2Icon } from '../../../svg/undo-2-icon';
import { cn } from '../../../utils/cn';

import { MonitorIcon } from '../../../svg/monitor-icon';
import { SmartphoneIcon } from '../../../svg/smartphone-icon';
import { ViewIcon } from '../../../svg/view-icon';
import type { MediaPickerComponent } from '../../editor/commands/button-image';
import {
  AvatarEditorContent,
  type AvatarEditorStyleUpdates,
} from './components/avatar/avatar-editor-content';
import { BlockMenu } from './components/block-menu';
import { BlockWrapper } from './components/block-wrapper';
import {
  ButtonEditorContent,
  type ButtonEditorStyleUpdates,
} from './components/button/button-editor-content';
import {
  ColumnsEditorContent,
  type ColumnsEditorStyleUpdates,
} from './components/columns/columns-editor-content';
import {
  ContainerEditorContent,
  type ContainerEditorStyleUpdates,
} from './components/container/container-editor-content';
import {
  DividerEditorContent,
  type DividerEditorStyleUpdates,
} from './components/divider/divider-editor-content';
import { ElementTree } from './components/element-tree';
import { GlobalStylesEditorContent } from './components/global-styles-editor-content';
import {
  HeadingEditorContent,
  type HeadingEditorStyleUpdates,
} from './components/heading/heading-editor-content';
import {
  HtmlEditorContent,
  type HtmlEditorStyleUpdates,
} from './components/html/html-editor-content';
import {
  ImageEditorContent,
  type ImageEditorStyleUpdates,
} from './components/image/image-editor-content';
import { SpacerEditorContent } from './components/spacer/spacer-editor-content';
import {
  TextEditorContent,
  type TextEditorStyleUpdates,
} from './components/text/text-editor-content';
import { type InsertableBlockType, NEW_BLOCKS } from './constants/blocks';
import { EMAIL_COLOR } from './constants/colors';
import { EditorEmailLayout } from './layout';
import {
  isAvatarBlock,
  isButtonBlock,
  isColumnsContainerBlock,
  isContainerBlock,
  isDividerBlock,
  isHeadingBlock,
  isHtmlBlock,
  isImageBlock,
  isSpacerBlock,
  isTextBlock,
} from './utils/block-guards';
import { getFontFamily } from './utils/email-styles';
import { normalizeBlockDocument } from './utils/normalize-block-document';

type EditorEmailProps = {
  mediaPicker?: ComponentType<MediaPickerComponent>;
  document: TReaderDocument;
  isBlock: boolean;
  emailBlocksElement?: JSX.Element;
  rootBlockId: string;
  theme: 'light' | 'dark';
  onDocumentChange: (nextDocument: TReaderDocument) => void;
  savedBlocks: {
    id: string;
    name: string;
    description?: string;
    key?: string;
    item?: string | JSX.Element;
    document: TReaderDocument;
  }[];
  onSaveBlock?: (payload: {
    name: string;
    description: string;
    document: TReaderDocument;
    html: string;
  }) => void | Promise<void>;
};

type TEditorEmailHistorySnapshot = {
  document: TReaderDocument;
  blockInsertionIds: Set<string>;
};

type TEditorEmailHistoryState = {
  pastSnapshots: TEditorEmailHistorySnapshot[];
  futureSnapshots: TEditorEmailHistorySnapshot[];
};

type PreviewMode = 'desktop' | 'mobile';

const HISTORY_LIMIT = 100;

function getEmailDownloadFilename(date = new Date()) {
  const guid = crypto.randomUUID().slice(0, 5);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `email-${day}-${month}-${year}-${guid}.html`;
}

function normalizeFontWeight(
  fontWeight: string | null | undefined,
): 'bold' | 'normal' | null | undefined {
  if (fontWeight === null) {
    return null;
  }

  if (fontWeight === 'bold' || fontWeight === 'normal') {
    return fontWeight;
  }

  return undefined;
}

function normalizeButtonStyle(
  buttonStyle: string | null | undefined,
): 'rounded' | 'rectangle' | 'pill' {
  if (buttonStyle === 'rectangle' || buttonStyle === 'rounded' || buttonStyle === 'pill') {
    return buttonStyle;
  }

  return 'pill';
}

function normalizeAvatarShape(shape: string | null | undefined): 'circle' | 'rounded' | 'square' {
  if (shape === 'circle' || shape === 'rounded' || shape === 'square') {
    return shape;
  }

  return 'circle';
}

type ReaderBlock = TReaderDocument[string];
type EmailLayoutBlock = Extract<ReaderBlock, { type: 'EmailLayout' }>;
type HeadingBlock = Extract<ReaderBlock, { type: 'Heading' }>;
type TextBlock = Extract<ReaderBlock, { type: 'Text' }>;
type ButtonBlock = Extract<ReaderBlock, { type: 'Button' }>;
type ImageBlock = Extract<ReaderBlock, { type: 'Image' }>;
type AvatarBlock = Extract<ReaderBlock, { type: 'Avatar' }>;
type DividerBlock = Extract<ReaderBlock, { type: 'Divider' }>;
type SpacerBlock = Extract<ReaderBlock, { type: 'Spacer' }>;
type HtmlBlock = Extract<ReaderBlock, { type: 'Html' }>;
type ColumnsContainerBlock = Extract<ReaderBlock, { type: 'ColumnsContainer' }>;
type ContainerBlock = Extract<ReaderBlock, { type: 'Container' }>;

type HeadingData = HeadingBlock['data'];

type HeadingProps = NonNullable<HeadingData['props']>;
type HeadingStyle = NonNullable<HeadingData['style']>;
type HeadingStylePadding = NonNullable<HeadingStyle['padding']>;

type TextData = TextBlock['data'];
type TextProps = NonNullable<TextData['props']>;
type TextStyle = NonNullable<TextData['style']>;
type TextStylePadding = NonNullable<TextStyle['padding']>;

type ButtonData = ButtonBlock['data'];
type ButtonProps = NonNullable<ButtonData['props']>;
type ButtonStyle = NonNullable<ButtonData['style']>;
type ButtonStylePadding = NonNullable<ButtonStyle['padding']>;

type ImageData = ImageBlock['data'];
type ImageProps = NonNullable<ImageData['props']>;
type ImageStyle = NonNullable<ImageData['style']>;
type ImageStylePadding = NonNullable<ImageStyle['padding']>;

type AvatarData = AvatarBlock['data'];
type AvatarProps = NonNullable<AvatarData['props']>;
type AvatarStyle = NonNullable<AvatarData['style']>;
type AvatarStylePadding = NonNullable<AvatarStyle['padding']>;

type DividerData = DividerBlock['data'];
type DividerProps = NonNullable<DividerData['props']>;
type DividerStyle = NonNullable<DividerData['style']>;
type DividerStylePadding = NonNullable<DividerStyle['padding']>;

type SpacerData = SpacerBlock['data'];
type SpacerProps = NonNullable<SpacerData['props']>;

type HtmlData = HtmlBlock['data'];
type HtmlProps = NonNullable<HtmlData['props']>;
type HtmlStyle = NonNullable<HtmlData['style']>;
type HtmlStylePadding = NonNullable<HtmlStyle['padding']>;

type ColumnsData = ColumnsContainerBlock['data'];
type ColumnsStyle = NonNullable<ColumnsData['style']>;
type ColumnsStylePadding = NonNullable<ColumnsStyle['padding']>;

type ContainerStyle = NonNullable<ContainerData['style']>;
type ContainerStylePadding = NonNullable<ContainerStyle['padding']>;

type EmailLayoutData = EmailLayoutBlock['data'];

type ContainerData = ContainerBlock['data'];

type ColumnsChildrenTuple = [
  { childrenIds: string[] },
  { childrenIds: string[] },
  { childrenIds: string[] },
];

type ColumnsContainerData = ColumnsContainerBlock['data'];

type BlockListRef =
  | {
      kind: 'root';
      ownerBlockId: string;
    }
  | {
      kind: 'container';
      ownerBlockId: string;
    }
  | {
      kind: 'column';
      ownerBlockId: string;
      columnIndex: 0 | 1 | 2;
    };

function getBlockListId(listRef: BlockListRef) {
  switch (listRef.kind) {
    case 'root':
      return `root:${listRef.ownerBlockId}`;
    case 'container':
      return `container:${listRef.ownerBlockId}`;
    case 'column':
      return `column:${listRef.ownerBlockId}:${listRef.columnIndex}`;
  }
}

function BlockChildrenList({
  listRef,
  childIds,
  className,
  children,
  isDropDisabled = false,
}: {
  listRef: BlockListRef;
  childIds: string[];
  className?: string;
  children: ReactNode;
  isDropDisabled?: boolean;
}) {
  const listId = getBlockListId(listRef);
  const { setNodeRef, isOver } = useDroppable({
    id: listId,
    data: { listRef },
    disabled: isDropDisabled,
  });

  return (
    <SortableContext items={childIds} strategy={verticalListSortingStrategy}>
      <div
        ref={setNodeRef}
        data-block-list-id={listId}
        className={cn('relative min-h-8 rounded-md', className, {
          'outline outline-accent/30 outline-dashed': isOver,
        })}
      >
        {children}
      </div>
    </SortableContext>
  );
}

function getColumnListClassName(contentAlignment?: 'top' | 'middle' | 'bottom' | null) {
  switch (contentAlignment) {
    case 'top':
      return 'flex h-full flex-col justify-start';
    case 'bottom':
      return 'flex h-full flex-col justify-end';
    case 'middle':
    default:
      return 'flex h-full flex-col justify-center';
  }
}

function EmptyBlockArea({ className, children }: { className?: string; children?: ReactNode }) {
  return (
    <div
      className={cn(
        'flex min-h-24 w-full items-center justify-center rounded-sm bg-muted',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function EditorEmail({
  document,
  isBlock,
  emailBlocksElement,
  mediaPicker,
  rootBlockId,
  theme,
  onDocumentChange,
  savedBlocks,
  onSaveBlock,
}: EditorEmailProps) {
  const initialHistoryState: TEditorEmailHistoryState = {
    pastSnapshots: [],
    futureSnapshots: [],
  };
  const [selectedBlockId, setSelectedBlockId] = useState<Extract<
    keyof TReaderDocument,
    string
  > | null>(null);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('mobile');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [blockInsertionIds, setBlockInsertionIds] = useState<Set<string>>(new Set());
  const [historyState, setHistoryState] = useState<TEditorEmailHistoryState>(initialHistoryState);
  const historyStateRef = useRef<TEditorEmailHistoryState>(initialHistoryState);
  const blockInsertionIdsRef = useRef<Set<string>>(new Set());
  const lastDocumentRef = useRef(document);
  const isApplyingHistoryRef = useRef(false);

  const canUndo = historyState.pastSnapshots.length > 0;
  const canRedo = historyState.futureSnapshots.length > 0;

  const normalizeDocument = useCallback(
    (nextDocument: TReaderDocument) =>
      isBlock ? normalizeBlockDocument(nextDocument, rootBlockId) : nextDocument,
    [isBlock, rootBlockId],
  );

  const updateBlockInsertionIds = useCallback(
    (updater: (currentIds: Set<string>) => Set<string>) => {
      const nextIds = updater(blockInsertionIdsRef.current);
      blockInsertionIdsRef.current = nextIds;
      setBlockInsertionIds(nextIds);
    },
    [],
  );

  const restoreBlockInsertionIds = useCallback((ids: Set<string>) => {
    const nextIds = new Set(ids);
    blockInsertionIdsRef.current = nextIds;
    setBlockInsertionIds(nextIds);
  }, []);

  const applyDocumentChange = useCallback(
    (nextDocument: TReaderDocument) => {
      const normalizedDocument = normalizeDocument(nextDocument);

      if (normalizedDocument === document || compare(normalizedDocument, document)) {
        return;
      }

      const nextHistoryState = {
        pastSnapshots: [
          ...historyStateRef.current.pastSnapshots.slice(-(HISTORY_LIMIT - 1)),
          {
            document,
            blockInsertionIds: new Set(blockInsertionIdsRef.current),
          },
        ],
        futureSnapshots: [],
      };

      historyStateRef.current = nextHistoryState;
      setHistoryState(nextHistoryState);
      lastDocumentRef.current = normalizedDocument;
      onDocumentChange(normalizedDocument);
    },
    [document, normalizeDocument, onDocumentChange],
  );

  const undo = useCallback(() => {
    const currentHistoryState = historyStateRef.current;

    if (currentHistoryState.pastSnapshots.length === 0) {
      return;
    }

    const previousSnapshot =
      currentHistoryState.pastSnapshots[currentHistoryState.pastSnapshots.length - 1];
    const nextHistoryState = {
      pastSnapshots: currentHistoryState.pastSnapshots.slice(0, -1),
      futureSnapshots: [
        {
          document,
          blockInsertionIds: new Set(blockInsertionIdsRef.current),
        },
        ...currentHistoryState.futureSnapshots,
      ].slice(0, HISTORY_LIMIT),
    };

    historyStateRef.current = nextHistoryState;
    setHistoryState(nextHistoryState);
    restoreBlockInsertionIds(previousSnapshot.blockInsertionIds);

    const previousDocument = normalizeDocument(previousSnapshot.document);
    isApplyingHistoryRef.current = true;
    lastDocumentRef.current = previousDocument;
    onDocumentChange(previousDocument);
  }, [document, normalizeDocument, onDocumentChange, restoreBlockInsertionIds]);

  const redo = useCallback(() => {
    const currentHistoryState = historyStateRef.current;

    if (currentHistoryState.futureSnapshots.length === 0) {
      return;
    }

    const [nextSnapshot, ...remainingFutureSnapshots] = currentHistoryState.futureSnapshots;
    const nextHistoryState = {
      pastSnapshots: [
        ...currentHistoryState.pastSnapshots.slice(-(HISTORY_LIMIT - 1)),
        {
          document,
          blockInsertionIds: new Set(blockInsertionIdsRef.current),
        },
      ],
      futureSnapshots: remainingFutureSnapshots,
    };

    historyStateRef.current = nextHistoryState;
    setHistoryState(nextHistoryState);
    restoreBlockInsertionIds(nextSnapshot.blockInsertionIds);

    const nextDocument = normalizeDocument(nextSnapshot.document);
    isApplyingHistoryRef.current = true;
    lastDocumentRef.current = nextDocument;
    onDocumentChange(nextDocument);
  }, [document, normalizeDocument, onDocumentChange, restoreBlockInsertionIds]);

  useEffect(() => {
    if (selectedBlockId && !document[selectedBlockId]) {
      setSelectedBlockId(null);
    }
  }, [document, selectedBlockId]);

  useEffect(() => {
    if (document === lastDocumentRef.current || compare(document, lastDocumentRef.current)) {
      isApplyingHistoryRef.current = false;
      lastDocumentRef.current = document;
      return;
    }

    if (isApplyingHistoryRef.current) {
      isApplyingHistoryRef.current = false;
      lastDocumentRef.current = document;
      return;
    }

    // External document resets should invalidate local undo/redo stacks.
    lastDocumentRef.current = document;
    historyStateRef.current = initialHistoryState;
    setHistoryState(initialHistoryState);
    restoreBlockInsertionIds(new Set());
  }, [document, restoreBlockInsertionIds]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isUndoShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z';
      const isRedoShortcut =
        (event.metaKey || event.ctrlKey) &&
        (event.shiftKey ? event.key.toLowerCase() === 'z' : event.key.toLowerCase() === 'y');

      if (isUndoShortcut && !event.shiftKey) {
        if (!canUndo) return;
        event.preventDefault();
        undo();
        return;
      }

      if (isRedoShortcut) {
        if (!canRedo) return;
        event.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [canRedo, canUndo, redo, undo]);

  const selectedBlock = selectedBlockId ? document[selectedBlockId] : undefined;
  const rootBlock = document[rootBlockId];
  const selectedEmailLayoutData =
    selectedBlockId === rootBlockId && selectedBlock?.type === 'EmailLayout'
      ? selectedBlock.data
      : undefined;
  const selectedHeadingData = isHeadingBlock(selectedBlock)
    ? (selectedBlock.data as HeadingData)
    : undefined;
  const selectedTextData = isTextBlock(selectedBlock)
    ? (selectedBlock.data as TextData)
    : undefined;
  const selectedButtonData = isButtonBlock(selectedBlock)
    ? (selectedBlock.data as ButtonData)
    : undefined;
  const selectedImageData = isImageBlock(selectedBlock)
    ? (selectedBlock.data as ImageData)
    : undefined;
  const selectedAvatarData = isAvatarBlock(selectedBlock)
    ? (selectedBlock.data as AvatarData)
    : undefined;
  const selectedDividerData = isDividerBlock(selectedBlock)
    ? (selectedBlock.data as DividerData)
    : undefined;
  const selectedSpacerData = isSpacerBlock(selectedBlock)
    ? (selectedBlock.data as SpacerData)
    : undefined;
  const selectedHtmlData = isHtmlBlock(selectedBlock)
    ? (selectedBlock.data as HtmlData)
    : undefined;
  const selectedColumnsData = isColumnsContainerBlock(selectedBlock)
    ? (selectedBlock.data as ColumnsContainerData)
    : undefined;
  const selectedContainerData = isContainerBlock(selectedBlock)
    ? (selectedBlock.data as ContainerData)
    : undefined;

  const rootListRef = useMemo<BlockListRef>(
    () => ({ kind: 'root', ownerBlockId: rootBlockId }),
    [rootBlockId],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const getBlockChildrenIds = useCallback(
    (blockId: string): string[] => {
      const block = document[blockId];

      if (!block) {
        return [];
      }

      switch (block.type) {
        case 'EmailLayout':
          return block.data.childrenIds ?? [];
        case 'Container':
          return block.data?.props?.childrenIds ?? [];
        case 'ColumnsContainer':
          return (block.data?.props?.columns ?? []).flatMap((column) => column.childrenIds);
        default:
          return [];
      }
    },
    [document],
  );

  const getListChildrenIds = useCallback(
    (listRef: BlockListRef): string[] => {
      const block = document[listRef.ownerBlockId];

      if (!block) {
        return [];
      }

      switch (listRef.kind) {
        case 'root':
          return block.type === 'EmailLayout' ? (block.data.childrenIds ?? []) : [];
        case 'container':
          return block.type === 'Container' ? (block.data?.props?.childrenIds ?? []) : [];
        case 'column':
          if (block.type !== 'ColumnsContainer') {
            return [];
          }

          return block.data?.props?.columns?.[listRef.columnIndex]?.childrenIds ?? [];
      }
    },
    [document],
  );

  const setListChildrenIds = useCallback(
    (nextDocument: TReaderDocument, listRef: BlockListRef, nextChildrenIds: string[]) => {
      const block = nextDocument[listRef.ownerBlockId];

      if (!block) {
        return;
      }

      switch (listRef.kind) {
        case 'root':
          if (block.type !== 'EmailLayout') {
            return;
          }

          nextDocument[listRef.ownerBlockId] = {
            ...block,
            data: {
              ...block.data,
              childrenIds: nextChildrenIds,
            },
          };
          return;
        case 'container':
          if (block.type !== 'Container') {
            return;
          }

          nextDocument[listRef.ownerBlockId] = {
            ...block,
            data: {
              ...block.data,
              props: {
                ...(block.data?.props ?? {}),
                childrenIds: nextChildrenIds,
              },
            },
          };
          return;
        case 'column':
          if (block.type !== 'ColumnsContainer') {
            return;
          }

          nextDocument[listRef.ownerBlockId] = {
            ...block,
            data: {
              ...block.data,
              props: {
                ...(block.data?.props ?? {}),
                columns: (block.data?.props?.columns ?? []).map((column, columnIndex) =>
                  columnIndex === listRef.columnIndex
                    ? { ...column, childrenIds: nextChildrenIds }
                    : column,
                ) as ColumnsChildrenTuple,
              },
            },
          };
      }
    },
    [],
  );

  const parseListId = useCallback((listId: string): BlockListRef | null => {
    const [kind, ownerBlockId, columnIndex] = listId.split(':');

    if (kind === 'root' && ownerBlockId) {
      return { kind: 'root', ownerBlockId };
    }

    if (kind === 'container' && ownerBlockId) {
      return { kind: 'container', ownerBlockId };
    }

    if (
      kind === 'column' &&
      ownerBlockId &&
      (columnIndex === '0' || columnIndex === '1' || columnIndex === '2')
    ) {
      return {
        kind: 'column',
        ownerBlockId,
        columnIndex: Number(columnIndex) as 0 | 1 | 2,
      };
    }

    return null;
  }, []);

  const isBlockDescendantOf = useCallback(
    (ancestorBlockId: string, candidateBlockId: string): boolean => {
      if (ancestorBlockId === candidateBlockId) {
        return true;
      }

      for (const childId of getBlockChildrenIds(ancestorBlockId)) {
        if (childId === candidateBlockId || isBlockDescendantOf(childId, candidateBlockId)) {
          return true;
        }
      }

      return false;
    },
    [getBlockChildrenIds],
  );

  const collisionDetection = useCallback<CollisionDetection>((args) => {
    if (!args.pointerCoordinates) {
      return closestCenter(args);
    }

    const withinCollisions = pointerWithin(args);
    const listCollisions = withinCollisions.filter(({ id }) => {
      const droppable = args.droppableContainers.find((container) => container.id === id);
      return Boolean((droppable?.data.current as { listRef?: BlockListRef } | undefined)?.listRef);
    });

    if (listCollisions.length === 0) {
      return [];
    }

    const innermostListCollision = listCollisions.reduce((best, candidate) => {
      const bestRect = args.droppableRects.get(best.id);
      const candidateRect = args.droppableRects.get(candidate.id);

      if (!bestRect || !candidateRect) {
        return best;
      }

      return candidateRect.width * candidateRect.height < bestRect.width * bestRect.height
        ? candidate
        : best;
    });

    const innermostListId = String(innermostListCollision.id);
    const childCollisions = withinCollisions.filter(({ id }) => {
      if (
        String(id).startsWith('root:') ||
        String(id).startsWith('container:') ||
        String(id).startsWith('column:')
      ) {
        return false;
      }

      const droppable = args.droppableContainers.find((container) => container.id === id);
      const data = droppable?.data.current as { parentListId?: string } | undefined;
      return data?.parentListId === innermostListId;
    });

    if (childCollisions.length > 0) {
      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter((container) =>
          childCollisions.some((collision) => collision.id === container.id),
        ),
      });
    }

    return [innermostListCollision];
  }, []);

  const moveBlock = useCallback(
    (
      activeBlockId: string,
      sourceListRef: BlockListRef,
      targetListRef: BlockListRef,
      overId: string | null,
    ) => {
      const sourceListId = getBlockListId(sourceListRef);
      const targetListId = getBlockListId(targetListRef);

      if (isBlock && targetListRef.kind === 'root' && sourceListRef.kind !== 'root') {
        return;
      }

      if (isBlockDescendantOf(activeBlockId, targetListRef.ownerBlockId)) {
        return;
      }

      const sourceChildrenIds = getListChildrenIds(sourceListRef);
      const sourceIndex = sourceChildrenIds.indexOf(activeBlockId);

      if (sourceIndex < 0) {
        return;
      }

      const nextDocument = { ...document };

      if (sourceListId === targetListId) {
        if (!overId || overId === targetListId) {
          return;
        }

        const targetIndex = sourceChildrenIds.indexOf(overId);

        if (targetIndex < 0 || targetIndex === sourceIndex) {
          return;
        }

        setListChildrenIds(
          nextDocument,
          sourceListRef,
          arrayMove(sourceChildrenIds, sourceIndex, targetIndex),
        );
        applyDocumentChange(nextDocument);
        setSelectedBlockId(activeBlockId);
        return;
      }

      const targetChildrenIds = getListChildrenIds(targetListRef);
      const nextSourceChildrenIds = sourceChildrenIds.filter(
        (childId) => childId !== activeBlockId,
      );
      const targetIndex =
        overId && overId !== targetListId ? targetChildrenIds.indexOf(overId) : -1;
      const insertionIndex = targetIndex >= 0 ? targetIndex : targetChildrenIds.length;
      const nextTargetChildrenIds = [...targetChildrenIds];

      nextTargetChildrenIds.splice(insertionIndex, 0, activeBlockId);

      setListChildrenIds(nextDocument, sourceListRef, nextSourceChildrenIds);
      setListChildrenIds(nextDocument, targetListRef, nextTargetChildrenIds);
      applyDocumentChange(nextDocument);
      setSelectedBlockId(activeBlockId);
    },
    [
      applyDocumentChange,
      document,
      getListChildrenIds,
      isBlock,
      isBlockDescendantOf,
      setListChildrenIds,
    ],
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (!over) {
        return;
      }

      const activeBlockId = String(active.id);
      const activeData = active.data.current as { parentListId?: string } | undefined;
      const overData = over.data.current as
        { parentListId?: string; listRef?: BlockListRef } | undefined;
      const sourceListRef = activeData?.parentListId ? parseListId(activeData.parentListId) : null;
      const targetListRef =
        overData?.listRef ?? (overData?.parentListId ? parseListId(overData.parentListId) : null);

      if (!sourceListRef || !targetListRef) {
        return;
      }

      moveBlock(activeBlockId, sourceListRef, targetListRef, String(over.id));
    },
    [moveBlock, parseListId],
  );

  const deleteBlock = (blockId: string) => {
    const idsToDelete = new Set<string>();

    const collectChildrenIds = (currentBlock: TReaderDocument[string]) => {
      switch (currentBlock.type) {
        case 'EmailLayout':
          return currentBlock.data.childrenIds ?? [];
        case 'Container':
          return currentBlock.data?.props?.childrenIds ?? [];
        case 'ColumnsContainer':
          return (currentBlock.data?.props?.columns ?? []).flatMap((column) => column.childrenIds);
        default:
          return [];
      }
    };

    const collectBlockTree = (currentBlockId: string) => {
      if (idsToDelete.has(currentBlockId)) {
        return;
      }

      const currentBlock = document[currentBlockId];

      if (!currentBlock) {
        return;
      }

      idsToDelete.add(currentBlockId);
      collectChildrenIds(currentBlock).forEach(collectBlockTree);
    };

    collectBlockTree(blockId);

    if (idsToDelete.size === 0) {
      return;
    }

    const nextDocument = { ...document };

    for (const currentBlockId of Object.keys(nextDocument)) {
      if (idsToDelete.has(currentBlockId)) {
        delete nextDocument[currentBlockId];
        continue;
      }

      const currentBlock = nextDocument[currentBlockId];

      switch (currentBlock.type) {
        case 'EmailLayout':
          nextDocument[currentBlockId] = {
            ...currentBlock,
            data: {
              ...currentBlock.data,
              childrenIds: (currentBlock.data.childrenIds ?? []).filter(
                (childId) => !idsToDelete.has(childId),
              ),
            },
          };
          break;
        case 'Container':
          nextDocument[currentBlockId] = {
            ...currentBlock,
            data: {
              ...currentBlock.data,
              props: {
                ...currentBlock.data?.props,
                childrenIds: (currentBlock.data?.props?.childrenIds ?? []).filter(
                  (childId) => !idsToDelete.has(childId),
                ),
              },
            },
          };
          break;
        case 'ColumnsContainer':
          {
            const columnsData = currentBlock.data as ColumnsContainerData;
            const nextColumnsData: ColumnsContainerData = {
              ...columnsData,
              props: {
                ...columnsData.props,
                columns: [
                  {
                    ...columnsData.props?.columns?.[0],
                    childrenIds: (columnsData.props?.columns?.[0]?.childrenIds ?? []).filter(
                      (childId) => !idsToDelete.has(childId),
                    ),
                  },
                  {
                    ...columnsData.props?.columns?.[1],
                    childrenIds: (columnsData.props?.columns?.[1]?.childrenIds ?? []).filter(
                      (childId) => !idsToDelete.has(childId),
                    ),
                  },
                  {
                    ...columnsData.props?.columns?.[2],
                    childrenIds: (columnsData.props?.columns?.[2]?.childrenIds ?? []).filter(
                      (childId) => !idsToDelete.has(childId),
                    ),
                  },
                ],
              },
            };

            nextDocument[currentBlockId] = {
              ...currentBlock,
              data: nextColumnsData,
            };
          }
          break;
      }
    }

    if (selectedBlockId && idsToDelete.has(selectedBlockId)) {
      setSelectedBlockId(null);
    }

    applyDocumentChange(nextDocument);
    updateBlockInsertionIds(
      (ids) => new Set([...ids].filter((blockId) => !idsToDelete.has(blockId))),
    );
  };

  const duplicateBlock = (blockId: string, parentListRef: BlockListRef) => {
    const currentChildrenIds = getListChildrenIds(parentListRef);
    const currentIndex = currentChildrenIds.indexOf(blockId);

    if (currentIndex < 0 || !document[blockId]) {
      return;
    }

    const nextDocument = { ...document };
    const generatedIds = new Set<string>();
    const createBlockId = () => {
      let nextBlockId = '';

      do {
        nextBlockId = `block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      } while (nextDocument[nextBlockId] || generatedIds.has(nextBlockId));

      generatedIds.add(nextBlockId);
      return nextBlockId;
    };
    const cloneBlockTree = (currentBlockId: string): string => {
      const currentBlock = document[currentBlockId];
      const clonedBlockId = createBlockId();
      const clonedBlock = structuredClone(currentBlock);

      switch (clonedBlock.type) {
        case 'Container':
          clonedBlock.data.props = {
            ...(clonedBlock.data.props ?? {}),
            childrenIds: (clonedBlock.data.props?.childrenIds ?? []).map(cloneBlockTree),
          };
          break;
        case 'ColumnsContainer':
          clonedBlock.data.props = {
            ...(clonedBlock.data.props ?? {}),
            columns: (clonedBlock.data.props?.columns ?? []).map((column) => ({
              ...column,
              childrenIds: column.childrenIds.map(cloneBlockTree),
            })) as ColumnsChildrenTuple,
          };
          break;
      }

      nextDocument[clonedBlockId] = clonedBlock;
      return clonedBlockId;
    };

    const clonedBlockId = cloneBlockTree(blockId);
    const nextChildrenIds = [...currentChildrenIds];
    nextChildrenIds.splice(currentIndex + 1, 0, clonedBlockId);
    setListChildrenIds(nextDocument, parentListRef, nextChildrenIds);
    applyDocumentChange(nextDocument);
    setSelectedBlockId(clonedBlockId);
  };

  const buildNewBlock = useCallback(
    (blockType: InsertableBlockType): { blockId: string; block: TReaderDocument[string] } => {
      let blockId = '';

      do {
        blockId = `block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      } while (document[blockId]);

      return { blockId, block: NEW_BLOCKS[blockType] };
    },
    [document],
  );

  const insertBlockRelativeTo = useCallback(
    (
      currentBlockId: string,
      parentListRef: BlockListRef,
      position: 'before' | 'after',
      blockType: InsertableBlockType,
    ) => {
      const currentChildrenIds = getListChildrenIds(parentListRef);
      const currentIndex = currentChildrenIds.indexOf(currentBlockId);

      if (currentIndex < 0) {
        return;
      }

      const insertionIndex = position === 'before' ? currentIndex : currentIndex + 1;
      const { blockId: newBlockId, block: newBlock } = buildNewBlock(blockType);
      const nextChildrenIds = [...currentChildrenIds];

      nextChildrenIds.splice(insertionIndex, 0, newBlockId);

      const nextDocument = {
        ...document,
        [newBlockId]: newBlock,
      };

      setListChildrenIds(nextDocument, parentListRef, nextChildrenIds);
      applyDocumentChange(nextDocument);
      setSelectedBlockId(newBlockId);
      if (blockType === 'block') {
        updateBlockInsertionIds((ids) => new Set(ids).add(newBlockId));
      }
    },
    [
      applyDocumentChange,
      buildNewBlock,
      document,
      getListChildrenIds,
      setListChildrenIds,
      updateBlockInsertionIds,
    ],
  );

  const insertBlockIntoList = useCallback(
    (listRef: BlockListRef, blockType: InsertableBlockType) => {
      const currentChildrenIds = getListChildrenIds(listRef);
      const { blockId: newBlockId, block: newBlock } = buildNewBlock(blockType);
      const nextDocument = {
        ...document,
        [newBlockId]: newBlock,
      };

      setListChildrenIds(nextDocument, listRef, [...currentChildrenIds, newBlockId]);
      applyDocumentChange(nextDocument);
      setSelectedBlockId(newBlockId);
      if (blockType === 'block') {
        updateBlockInsertionIds((ids) => new Set(ids).add(newBlockId));
      }
    },
    [
      applyDocumentChange,
      buildNewBlock,
      document,
      getListChildrenIds,
      setListChildrenIds,
      updateBlockInsertionIds,
    ],
  );

  const headingEditorProps: ComponentProps<typeof HeadingEditorContent> | null =
    selectedHeadingData && selectedBlockId
      ? {
          text: selectedHeadingData.props?.text ?? '',
          level: selectedHeadingData.props?.level ?? 'h2',
          textColor: selectedHeadingData.style?.color ?? '',
          backgroundColor: selectedHeadingData.style?.backgroundColor ?? '',
          fontFamily: selectedHeadingData.style?.fontFamily ?? 'MODERN_SANS',
          fontWeight: selectedHeadingData.style?.fontWeight ?? 'bold',
          alignment:
            selectedHeadingData.style?.textAlign === 'center' ||
            selectedHeadingData.style?.textAlign === 'right'
              ? selectedHeadingData.style.textAlign
              : 'left',
          paddingTop: selectedHeadingData.style?.padding?.top ?? 0,
          margin: selectedHeadingData.style?.margin,
          paddingBottom: selectedHeadingData.style?.padding?.bottom ?? 0,
          paddingLeft: selectedHeadingData.style?.padding?.left ?? 0,
          paddingRight: selectedHeadingData.style?.padding?.right ?? 0,
          onTextChange: (value) => {
            updateHeading(selectedBlockId, { props: { text: value } });
          },
          onLevelChange: (value) => {
            if (value !== 'h1' && value !== 'h2' && value !== 'h3') {
              return;
            }

            updateHeading(selectedBlockId, { props: { level: value } });
          },
          onStyleChange: (value) => {
            updateHeading(selectedBlockId, { style: value });
          },
        }
      : null;

  const textEditorProps: ComponentProps<typeof TextEditorContent> | null =
    selectedTextData && selectedBlockId
      ? {
          text: selectedTextData.props?.text ?? '',
          textColor: selectedTextData.style?.color ?? '',
          backgroundColor: selectedTextData.style?.backgroundColor ?? '',
          fontFamily: selectedTextData.style?.fontFamily ?? 'MODERN_SANS',
          fontSize: selectedTextData.style?.fontSize ?? 16,
          fontWeight: selectedTextData.style?.fontWeight === 'bold' ? 'bold' : 'normal',
          alignment:
            selectedTextData.style?.textAlign === 'center' ||
            selectedTextData.style?.textAlign === 'right'
              ? selectedTextData.style.textAlign
              : 'left',
          paddingTop: selectedTextData.style?.padding?.top ?? 0,
          margin: selectedTextData.style?.margin,
          paddingBottom: selectedTextData.style?.padding?.bottom ?? 0,
          paddingLeft: selectedTextData.style?.padding?.left ?? 0,
          paddingRight: selectedTextData.style?.padding?.right ?? 0,
          onTextChange: (value) => {
            updateText(selectedBlockId, { props: { text: value } });
          },
          onStyleChange: (value) => {
            updateText(selectedBlockId, { style: value });
          },
        }
      : null;

  const buttonEditorProps: ComponentProps<typeof ButtonEditorContent> | null =
    selectedButtonData && selectedBlockId
      ? {
          text: selectedButtonData.props?.text ?? '',
          url: selectedButtonData.props?.url ?? '',
          width: selectedButtonData.props?.fullWidth ? 'full' : 'auto',
          size:
            selectedButtonData.props?.size === 'x-small'
              ? 'xs'
              : selectedButtonData.props?.size === 'small'
                ? 'sm'
                : selectedButtonData.props?.size === 'large'
                  ? 'lg'
                  : 'md',
          styleVariant: normalizeButtonStyle(selectedButtonData.props?.buttonStyle),
          textColor: selectedButtonData.props?.buttonTextColor ?? '',
          buttonColor: selectedButtonData.props?.buttonBackgroundColor ?? '',
          backgroundColor: selectedButtonData.style?.backgroundColor ?? '',
          fontFamily: selectedButtonData.style?.fontFamily ?? 'MODERN_SANS',
          fontSize: selectedButtonData.style?.fontSize ?? 16,
          fontWeight: selectedButtonData.style?.fontWeight === 'bold' ? 'bold' : 'normal',
          alignment:
            selectedButtonData.style?.textAlign === 'center' ||
            selectedButtonData.style?.textAlign === 'right'
              ? selectedButtonData.style.textAlign
              : 'left',
          paddingTop: selectedButtonData.style?.padding?.top ?? 0,
          margin: selectedButtonData.style?.margin,
          paddingBottom: selectedButtonData.style?.padding?.bottom ?? 0,
          paddingLeft: selectedButtonData.style?.padding?.left ?? 0,
          paddingRight: selectedButtonData.style?.padding?.right ?? 0,
          onTextChange: (value) => {
            updateButton(selectedBlockId, { props: { text: value } });
          },
          onUrlChange: (value) => {
            updateButton(selectedBlockId, { props: { url: value } });
          },
          onWidthChange: (value) => {
            updateButton(selectedBlockId, { props: { fullWidth: value === 'full' } });
          },
          onSizeChange: (value) => {
            updateButton(selectedBlockId, {
              props: {
                size:
                  value === 'xs'
                    ? 'x-small'
                    : value === 'sm'
                      ? 'small'
                      : value === 'lg'
                        ? 'large'
                        : 'medium',
              },
            });
          },
          onStyleVariantChange: (value) => {
            updateButton(selectedBlockId, {
              props: { buttonStyle: normalizeButtonStyle(value) },
            });
          },
          onStyleChange: (value) => {
            updateButton(selectedBlockId, { style: value });
          },
        }
      : null;

  const imageEditorProps: ComponentProps<typeof ImageEditorContent> | null =
    selectedImageData && selectedBlockId
      ? {
          mediaPicker,
          sourceUrl: selectedImageData.props?.url ?? '',
          altText: selectedImageData.props?.alt ?? '',
          clickThroughUrl: selectedImageData.props?.linkHref ?? '',
          width:
            typeof selectedImageData.props?.width === 'number'
              ? String(selectedImageData.props.width)
              : '',
          height:
            typeof selectedImageData.props?.height === 'number'
              ? String(selectedImageData.props.height)
              : '',
          verticalAlignment: selectedImageData.props?.contentAlignment ?? 'middle',
          backgroundColor: selectedImageData.style?.backgroundColor ?? '',
          borderRadius: selectedImageData.style?.borderRadius,
          horizontalAlignment:
            selectedImageData.style?.textAlign === 'center' ||
            selectedImageData.style?.textAlign === 'right'
              ? selectedImageData.style.textAlign
              : 'left',
          paddingTop: selectedImageData.style?.padding?.top ?? 0,
          margin: selectedImageData.style?.margin,
          paddingBottom: selectedImageData.style?.padding?.bottom ?? 0,
          paddingLeft: selectedImageData.style?.padding?.left ?? 0,
          paddingRight: selectedImageData.style?.padding?.right ?? 0,
          onSourceUrlChange: (value) => {
            updateImage(selectedBlockId, { props: { url: value || null } });
          },
          onAltTextChange: (value) => {
            updateImage(selectedBlockId, { props: { alt: value || null } });
          },
          onClickThroughUrlChange: (value) => {
            updateImage(selectedBlockId, { props: { linkHref: value || null } });
          },
          onWidthChange: (value) => {
            const parsedValue = Number(value);
            updateImage(selectedBlockId, {
              props: {
                width:
                  value === '' || !Number.isFinite(parsedValue) ? null : Math.max(1, parsedValue),
              },
            });
          },
          onHeightChange: (value) => {
            const parsedValue = Number(value);
            updateImage(selectedBlockId, {
              props: {
                height:
                  value === '' || !Number.isFinite(parsedValue) ? null : Math.max(1, parsedValue),
              },
            });
          },
          onStyleChange: (value) => {
            updateImage(selectedBlockId, { style: value });
          },
        }
      : null;

  const avatarEditorProps: ComponentProps<typeof AvatarEditorContent> | null =
    selectedAvatarData && selectedBlockId
      ? {
          size:
            typeof selectedAvatarData.props?.size === 'number'
              ? String(selectedAvatarData.props.size)
              : '',
          shape: normalizeAvatarShape(selectedAvatarData.props?.shape),
          imageUrl: selectedAvatarData.props?.imageUrl ?? '',
          altText: selectedAvatarData.props?.alt ?? '',
          horizontalAlignment:
            selectedAvatarData.style?.textAlign === 'center' ||
            selectedAvatarData.style?.textAlign === 'right'
              ? selectedAvatarData.style.textAlign
              : 'left',
          paddingTop: selectedAvatarData.style?.padding?.top ?? 0,
          margin: selectedAvatarData.style?.margin,
          paddingBottom: selectedAvatarData.style?.padding?.bottom ?? 0,
          paddingLeft: selectedAvatarData.style?.padding?.left ?? 0,
          paddingRight: selectedAvatarData.style?.padding?.right ?? 0,
          onSizeChange: (value) => {
            const parsedValue = Number(value);
            updateAvatar(selectedBlockId, {
              props: {
                size:
                  value === '' || !Number.isFinite(parsedValue) ? null : Math.max(1, parsedValue),
              },
            });
          },
          onShapeChange: (value) => {
            updateAvatar(selectedBlockId, { props: { shape: normalizeAvatarShape(value) } });
          },
          onImageUrlChange: (value) => {
            updateAvatar(selectedBlockId, { props: { imageUrl: value || null } });
          },
          onAltTextChange: (value) => {
            updateAvatar(selectedBlockId, { props: { alt: value || null } });
          },
          onStyleChange: (value) => {
            updateAvatar(selectedBlockId, { style: value });
          },
        }
      : null;

  const dividerEditorProps: ComponentProps<typeof DividerEditorContent> | null =
    selectedDividerData && selectedBlockId
      ? {
          color: selectedDividerData.props?.lineColor ?? '',
          height:
            typeof selectedDividerData.props?.lineHeight === 'number'
              ? String(selectedDividerData.props.lineHeight)
              : '',
          backgroundColor: selectedDividerData.style?.backgroundColor ?? '',
          paddingTop: selectedDividerData.style?.padding?.top ?? 0,
          margin: selectedDividerData.style?.margin,
          paddingBottom: selectedDividerData.style?.padding?.bottom ?? 0,
          paddingLeft: selectedDividerData.style?.padding?.left ?? 0,
          paddingRight: selectedDividerData.style?.padding?.right ?? 0,
          onHeightChange: (value) => {
            const parsedValue = Number(value);
            updateDivider(selectedBlockId, {
              props: {
                lineHeight:
                  value === '' || !Number.isFinite(parsedValue) ? null : Math.max(0, parsedValue),
              },
            });
          },
          onStyleChange: (value) => {
            updateDivider(selectedBlockId, { style: value });
          },
        }
      : null;

  const spacerEditorProps: ComponentProps<typeof SpacerEditorContent> | null =
    selectedSpacerData && selectedBlockId
      ? {
          height:
            typeof selectedSpacerData.props?.height === 'number'
              ? String(selectedSpacerData.props.height)
              : '',
          onHeightChange: (value) => {
            const parsedValue = Number(value);
            updateSpacer(selectedBlockId, {
              props: {
                height:
                  value === '' || !Number.isFinite(parsedValue) ? null : Math.max(0, parsedValue),
              },
            });
          },
        }
      : null;

  const htmlEditorProps: ComponentProps<typeof HtmlEditorContent> | null =
    selectedHtmlData && selectedBlockId
      ? {
          content: selectedHtmlData.props?.contents ?? '',
          textColor: selectedHtmlData.style?.color ?? '',
          backgroundColor: selectedHtmlData.style?.backgroundColor ?? '',
          fontFamily: selectedHtmlData.style?.fontFamily ?? 'MODERN_SANS',
          fontSize: selectedHtmlData.style?.fontSize ?? 16,
          alignment:
            selectedHtmlData.style?.textAlign === 'center' ||
            selectedHtmlData.style?.textAlign === 'right'
              ? selectedHtmlData.style.textAlign
              : 'left',
          paddingTop: selectedHtmlData.style?.padding?.top ?? 0,
          margin: selectedHtmlData.style?.margin,
          paddingBottom: selectedHtmlData.style?.padding?.bottom ?? 0,
          paddingLeft: selectedHtmlData.style?.padding?.left ?? 0,
          paddingRight: selectedHtmlData.style?.padding?.right ?? 0,
          theme,
          onContentChange: (value) => {
            updateHtml(selectedBlockId, { props: { contents: value || null } });
          },
          onStyleChange: (value) => {
            updateHtml(selectedBlockId, { style: value });
          },
        }
      : null;

  const columnsEditorProps: ComponentProps<typeof ColumnsEditorContent> | null =
    selectedColumnsData && selectedBlockId
      ? {
          columnsCount: selectedColumnsData.props?.columnsCount === 3 ? 3 : 2,
          column1Width:
            typeof selectedColumnsData.props?.fixedWidths?.[0] === 'number'
              ? String(selectedColumnsData.props.fixedWidths[0])
              : '',
          column2Width:
            typeof selectedColumnsData.props?.fixedWidths?.[1] === 'number'
              ? String(selectedColumnsData.props.fixedWidths[1])
              : '',
          column3Width:
            typeof selectedColumnsData.props?.fixedWidths?.[2] === 'number'
              ? String(selectedColumnsData.props.fixedWidths[2])
              : '',
          columnsGap:
            typeof selectedColumnsData.props?.columnsGap === 'number'
              ? String(selectedColumnsData.props.columnsGap)
              : '',
          verticalAlignment: selectedColumnsData.props?.contentAlignment ?? 'middle',
          backgroundColor: selectedColumnsData.style?.backgroundColor ?? '',
          paddingTop: selectedColumnsData.style?.padding?.top ?? 0,
          margin: selectedColumnsData.style?.margin,
          paddingBottom: selectedColumnsData.style?.padding?.bottom ?? 0,
          paddingLeft: selectedColumnsData.style?.padding?.left ?? 0,
          paddingRight: selectedColumnsData.style?.padding?.right ?? 0,
          onColumnsCountChange: (value) => {
            updateColumns(selectedBlockId, { props: { columnsCount: value } });
          },
          onColumn1WidthChange: (value) => {
            const parsedValue = Number(value);
            const currentFixedWidths = selectedColumnsData.props?.fixedWidths ?? [null, null, null];
            updateColumns(selectedBlockId, {
              props: {
                fixedWidths: [
                  value === '' || !Number.isFinite(parsedValue) ? null : Math.max(0, parsedValue),
                  currentFixedWidths[1] ?? null,
                  currentFixedWidths[2] ?? null,
                ],
              },
            });
          },
          onColumn2WidthChange: (value) => {
            const parsedValue = Number(value);
            const currentFixedWidths = selectedColumnsData.props?.fixedWidths ?? [null, null, null];
            updateColumns(selectedBlockId, {
              props: {
                fixedWidths: [
                  currentFixedWidths[0] ?? null,
                  value === '' || !Number.isFinite(parsedValue) ? null : Math.max(0, parsedValue),
                  currentFixedWidths[2] ?? null,
                ],
              },
            });
          },
          onColumn3WidthChange: (value) => {
            const parsedValue = Number(value);
            const currentFixedWidths = selectedColumnsData.props?.fixedWidths ?? [null, null, null];
            updateColumns(selectedBlockId, {
              props: {
                fixedWidths: [
                  currentFixedWidths[0] ?? null,
                  currentFixedWidths[1] ?? null,
                  value === '' || !Number.isFinite(parsedValue) ? null : Math.max(0, parsedValue),
                ],
              },
            });
          },
          onColumnsGapChange: (value) => {
            const parsedValue = Number(value);
            updateColumns(selectedBlockId, {
              props: {
                columnsGap:
                  value === '' || !Number.isFinite(parsedValue) ? null : Math.max(0, parsedValue),
              },
            });
          },
          onStyleChange: (value) => {
            updateColumns(selectedBlockId, { style: value });
          },
        }
      : null;

  const containerEditorProps: ComponentProps<typeof ContainerEditorContent> | null =
    selectedContainerData && selectedBlockId
      ? {
          mediaPicker,
          backgroundImage: selectedContainerData.style?.backgroundImage ?? '',
          backgroundColor: selectedContainerData.style?.backgroundColor ?? '',
          borderColor: selectedContainerData.style?.borderColor ?? '',
          borderStyle: selectedContainerData.style?.borderStyle,
          borderWidths: selectedContainerData.style?.borderWidths,
          borderRadius:
            typeof selectedContainerData.style?.borderRadius === 'number'
              ? selectedContainerData.style.borderRadius
              : null,
          paddingTop: selectedContainerData.style?.padding?.top ?? 0,
          margin: selectedContainerData.style?.margin,
          paddingBottom: selectedContainerData.style?.padding?.bottom ?? 0,
          paddingLeft: selectedContainerData.style?.padding?.left ?? 0,
          paddingRight: selectedContainerData.style?.padding?.right ?? 0,
          onStyleChange: (value) => {
            updateContainer(selectedBlockId, { style: value });
          },
          emailBlocksElement,
          blockOptions: blockInsertionIds.has(selectedBlockId)
            ? savedBlocks.map((savedBlock) => ({
                value: savedBlock.id,
                key: savedBlock.key,
                item: savedBlock.item ?? savedBlock.name,
              }))
            : undefined,
          onBlockChange: blockInsertionIds.has(selectedBlockId)
            ? (savedBlockId) => {
                const savedBlock = savedBlocks.find((item) => item.id === savedBlockId);
                if (savedBlock) replaceWithSavedBlock(selectedBlockId, savedBlock.document);
              }
            : undefined,
        }
      : null;

  const updateHeading = (
    headingId: string,
    updates: {
      props?: Partial<HeadingProps>;
      style?: HeadingEditorStyleUpdates;
    },
  ) => {
    const currentBlock = document[headingId];
    if (!isHeadingBlock(currentBlock)) {
      return;
    }

    const currentData = currentBlock.data as HeadingData;
    const currentStyle = (currentData.style ?? {}) as HeadingStyle;
    const currentPadding: HeadingStylePadding = currentStyle.padding ?? {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };
    const styleUpdates = updates.style;
    const {
      padding: paddingUpdates,
      fontWeight: nextFontWeightValue,
      ...otherStyleUpdates
    } = styleUpdates ?? {};
    const normalizedFontWeight = normalizeFontWeight(nextFontWeightValue);
    const nextStyle = updates.style
      ? {
          ...currentStyle,
          ...otherStyleUpdates,
          ...(nextFontWeightValue !== undefined ? { fontWeight: normalizedFontWeight } : {}),
          padding: paddingUpdates
            ? {
                ...currentPadding,
                ...paddingUpdates,
              }
            : currentStyle.padding,
        }
      : currentData.style;

    applyDocumentChange({
      ...document,
      [headingId]: {
        ...currentBlock,
        data: {
          ...currentData,
          props: {
            ...(currentData.props ?? {}),
            ...(updates.props ?? {}),
          },
          style: nextStyle,
        },
      },
    });
  };

  const updateText = (
    textBlockId: string,
    updates: {
      props?: Partial<TextProps>;
      style?: TextEditorStyleUpdates;
    },
  ) => {
    const currentBlock = document[textBlockId];
    if (!isTextBlock(currentBlock)) {
      return;
    }

    const currentData = currentBlock.data as TextData;
    const currentStyle = (currentData.style ?? {}) as TextStyle;
    const currentPadding: TextStylePadding = currentStyle.padding ?? {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };
    const styleUpdates = updates.style;
    const {
      padding: paddingUpdates,
      fontWeight: nextFontWeightValue,
      ...otherStyleUpdates
    } = styleUpdates ?? {};
    const normalizedFontWeight = normalizeFontWeight(nextFontWeightValue);
    const nextStyle = updates.style
      ? {
          ...currentStyle,
          ...otherStyleUpdates,
          ...(nextFontWeightValue !== undefined ? { fontWeight: normalizedFontWeight } : {}),
          padding: paddingUpdates
            ? {
                ...currentPadding,
                ...paddingUpdates,
              }
            : currentStyle.padding,
        }
      : currentData.style;

    applyDocumentChange({
      ...document,
      [textBlockId]: {
        ...currentBlock,
        data: {
          ...currentData,
          props: {
            ...(currentData.props ?? {}),
            ...(updates.props ?? {}),
          },
          style: nextStyle,
        },
      },
    });
  };

  const updateButton = (
    buttonBlockId: string,
    updates: {
      props?: Partial<ButtonProps>;
      style?: ButtonEditorStyleUpdates;
    },
  ) => {
    const currentBlock = document[buttonBlockId];
    if (!isButtonBlock(currentBlock)) {
      return;
    }

    const currentData = currentBlock.data as ButtonData;
    const currentStyle = (currentData.style ?? {}) as ButtonStyle;
    const currentPadding: ButtonStylePadding = currentStyle.padding ?? {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };
    const styleUpdates = updates.style;
    const {
      padding: paddingUpdates,
      fontWeight: nextFontWeightValue,
      buttonBackgroundColor: nextButtonBackgroundColor,
      buttonTextColor: nextButtonTextColor,
      ...otherStyleUpdates
    } = styleUpdates ?? {};
    const normalizedFontWeight = normalizeFontWeight(nextFontWeightValue);
    const nextProps = {
      ...(currentData.props ?? {}),
      ...(updates.props ?? {}),
    };
    if (nextButtonBackgroundColor !== undefined) {
      nextProps.buttonBackgroundColor = nextButtonBackgroundColor;
    }
    if (nextButtonTextColor !== undefined) {
      nextProps.buttonTextColor = nextButtonTextColor;
    }
    const nextStyle = updates.style
      ? {
          ...currentStyle,
          ...otherStyleUpdates,
          ...(nextFontWeightValue !== undefined ? { fontWeight: normalizedFontWeight } : {}),
          padding: paddingUpdates
            ? {
                ...currentPadding,
                ...paddingUpdates,
              }
            : currentStyle.padding,
        }
      : currentData.style;

    applyDocumentChange({
      ...document,
      [buttonBlockId]: {
        ...currentBlock,
        data: {
          ...currentData,
          props: nextProps,
          style: nextStyle,
        },
      },
    });
  };

  const updateImage = (
    imageBlockId: string,
    updates: {
      props?: Partial<ImageProps>;
      style?: ImageEditorStyleUpdates;
    },
  ) => {
    const currentBlock = document[imageBlockId];
    if (!isImageBlock(currentBlock)) {
      return;
    }

    const currentData = currentBlock.data as ImageData;
    const currentStyle = (currentData.style ?? {}) as ImageStyle;
    const currentPadding: ImageStylePadding = currentStyle.padding ?? {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };
    const styleUpdates = updates.style;
    const {
      padding: paddingUpdates,
      verticalAlignment: verticalAlignmentUpdate,
      ...otherStyleUpdates
    } = styleUpdates ?? {};
    const nextProps = {
      ...(currentData.props ?? {}),
      ...(updates.props ?? {}),
    };
    if (verticalAlignmentUpdate !== undefined) {
      nextProps.contentAlignment = verticalAlignmentUpdate;
    }
    const nextStyle = updates.style
      ? {
          ...currentStyle,
          ...otherStyleUpdates,
          padding: paddingUpdates
            ? {
                ...currentPadding,
                ...paddingUpdates,
              }
            : currentStyle.padding,
        }
      : currentData.style;

    applyDocumentChange({
      ...document,
      [imageBlockId]: {
        ...currentBlock,
        data: {
          ...currentData,
          props: nextProps,
          style: nextStyle,
        },
      },
    });
  };

  const updateAvatar = (
    avatarBlockId: string,
    updates: {
      props?: Partial<AvatarProps>;
      style?: AvatarEditorStyleUpdates;
    },
  ) => {
    const currentBlock = document[avatarBlockId];
    if (!isAvatarBlock(currentBlock)) {
      return;
    }

    const currentData = currentBlock.data as AvatarData;
    const currentStyle = (currentData.style ?? {}) as AvatarStyle;
    const currentPadding: AvatarStylePadding = currentStyle.padding ?? {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };
    const styleUpdates = updates.style;
    const { padding: paddingUpdates, ...otherStyleUpdates } = styleUpdates ?? {};
    const nextStyle = updates.style
      ? {
          ...currentStyle,
          ...otherStyleUpdates,
          padding: paddingUpdates
            ? {
                ...currentPadding,
                ...paddingUpdates,
              }
            : currentStyle.padding,
        }
      : currentData.style;

    applyDocumentChange({
      ...document,
      [avatarBlockId]: {
        ...currentBlock,
        data: {
          ...currentData,
          props: {
            ...(currentData.props ?? {}),
            ...(updates.props ?? {}),
          },
          style: nextStyle,
        },
      },
    });
  };

  const updateDivider = (
    dividerBlockId: string,
    updates: {
      props?: Partial<DividerProps>;
      style?: DividerEditorStyleUpdates;
    },
  ) => {
    const currentBlock = document[dividerBlockId];
    if (!isDividerBlock(currentBlock)) {
      return;
    }

    const currentData = currentBlock.data as DividerData;
    const currentStyle = (currentData.style ?? {}) as DividerStyle;
    const currentPadding: DividerStylePadding = currentStyle.padding ?? {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };
    const styleUpdates = updates.style;
    const {
      padding: paddingUpdates,
      lineColor: lineColorUpdate,
      ...otherStyleUpdates
    } = styleUpdates ?? {};
    const nextProps = {
      ...(currentData.props ?? {}),
      ...(updates.props ?? {}),
    };
    if (lineColorUpdate !== undefined) {
      nextProps.lineColor = lineColorUpdate;
    }
    const nextStyle = updates.style
      ? {
          ...currentStyle,
          ...otherStyleUpdates,
          padding: paddingUpdates
            ? {
                ...currentPadding,
                ...paddingUpdates,
              }
            : currentStyle.padding,
        }
      : currentData.style;

    applyDocumentChange({
      ...document,
      [dividerBlockId]: {
        ...currentBlock,
        data: {
          ...currentData,
          props: nextProps,
          style: nextStyle,
        },
      },
    });
  };

  const updateSpacer = (
    spacerBlockId: string,
    updates: {
      props?: Partial<SpacerProps>;
    },
  ) => {
    const currentBlock = document[spacerBlockId];
    if (!isSpacerBlock(currentBlock)) {
      return;
    }

    const currentData = currentBlock.data as SpacerData;

    applyDocumentChange({
      ...document,
      [spacerBlockId]: {
        ...currentBlock,
        data: {
          ...currentData,
          props: {
            ...(currentData.props ?? {}),
            ...(updates.props ?? {}),
          },
        },
      },
    });
  };

  const updateHtml = (
    htmlBlockId: string,
    updates: {
      props?: Partial<HtmlProps>;
      style?: HtmlEditorStyleUpdates;
    },
  ) => {
    const currentBlock = document[htmlBlockId];
    if (!isHtmlBlock(currentBlock)) {
      return;
    }

    const currentData = currentBlock.data as HtmlData;
    const currentStyle = (currentData.style ?? {}) as HtmlStyle;
    const currentPadding: HtmlStylePadding = currentStyle.padding ?? {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };
    const styleUpdates = updates.style;
    const { padding: paddingUpdates, ...otherStyleUpdates } = styleUpdates ?? {};
    const nextStyle = updates.style
      ? {
          ...currentStyle,
          ...otherStyleUpdates,
          padding: paddingUpdates
            ? {
                ...currentPadding,
                ...paddingUpdates,
              }
            : currentStyle.padding,
        }
      : currentData.style;

    applyDocumentChange({
      ...document,
      [htmlBlockId]: {
        ...currentBlock,
        data: {
          ...currentData,
          props: {
            ...(currentData.props ?? {}),
            ...(updates.props ?? {}),
          },
          style: nextStyle,
        },
      },
    });
  };

  const updateColumns = (
    columnsBlockId: string,
    updates: {
      props?: {
        fixedWidths?:
          [number | null | undefined, number | null | undefined, number | null | undefined] | null;
        columnsCount?: 2 | 3 | null;
        columnsGap?: number | null;
        contentAlignment?: 'top' | 'middle' | 'bottom' | null;
      };
      style?: ColumnsEditorStyleUpdates;
    },
  ) => {
    const currentBlock = document[columnsBlockId];
    if (!isColumnsContainerBlock(currentBlock)) {
      return;
    }

    const currentData = currentBlock.data as ColumnsContainerData;
    const currentStyle = (currentData.style ?? {}) as ColumnsStyle;
    const currentColumns: [
      { childrenIds: string[] },
      { childrenIds: string[] },
      { childrenIds: string[] },
    ] = [
      currentData.props?.columns?.[0] ?? { childrenIds: [] },
      currentData.props?.columns?.[1] ?? { childrenIds: [] },
      currentData.props?.columns?.[2] ?? { childrenIds: [] },
    ];
    const nextProps = {
      ...(currentData.props ?? {}),
      ...(updates.props ?? {}),
      columns: currentColumns,
    };
    const currentPadding: ColumnsStylePadding = currentStyle.padding ?? {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };
    const styleUpdates = updates.style;
    const {
      padding: paddingUpdates,
      verticalAlignment: verticalAlignmentUpdate,
      ...otherStyleUpdates
    } = styleUpdates ?? {};
    if (verticalAlignmentUpdate !== undefined) {
      nextProps.contentAlignment = verticalAlignmentUpdate;
    }
    const nextStyle = updates.style
      ? {
          ...currentStyle,
          ...otherStyleUpdates,
          padding: paddingUpdates
            ? {
                ...currentPadding,
                ...paddingUpdates,
              }
            : currentStyle.padding,
        }
      : currentData.style;

    applyDocumentChange({
      ...document,
      [columnsBlockId]: {
        ...currentBlock,
        data: {
          ...currentData,
          props: nextProps,
          style: nextStyle,
        },
      },
    });
  };

  const updateContainer = (
    containerBlockId: string,
    updates: {
      style?: ContainerEditorStyleUpdates;
    },
  ) => {
    const currentBlock = document[containerBlockId];
    if (!isContainerBlock(currentBlock)) {
      return;
    }

    const currentData = currentBlock.data as ContainerData;
    const currentStyle = (currentData.style ?? {}) as ContainerStyle;
    const currentPadding: ContainerStylePadding = currentStyle.padding ?? {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    };
    const styleUpdates = updates.style;
    const { padding: paddingUpdates, ...otherStyleUpdates } = styleUpdates ?? {};
    const nextStyle = updates.style
      ? {
          ...currentStyle,
          ...otherStyleUpdates,
          padding: paddingUpdates
            ? {
                ...currentPadding,
                ...paddingUpdates,
              }
            : currentStyle.padding,
        }
      : currentData.style;

    applyDocumentChange({
      ...document,
      [containerBlockId]: {
        ...currentBlock,
        data: {
          ...currentData,
          style: nextStyle,
        },
      },
    });
  };

  const getSavedBlockDocument = (containerId: string): TReaderDocument => {
    const nextDocument: TReaderDocument = {
      root: {
        type: 'EmailLayout',
        data: {
          backdropColor: EMAIL_COLOR.background,
          borderRadius: 0,
          canvasColor: EMAIL_COLOR.primaryInverted,
          textColor: EMAIL_COLOR.primary,
          fontFamily: 'MODERN_SANS',
          childrenIds: [containerId],
        },
      },
    };

    const copyTree = (blockId: string) => {
      const block = document[blockId];
      if (!block) return;
      nextDocument[blockId] = structuredClone(block);
      getBlockChildrenIds(blockId).forEach(copyTree);
    };

    copyTree(containerId);
    return nextDocument;
  };

  const findParentList = (childId: string): BlockListRef | null => {
    const visit = (ownerBlockId: string, listRef: BlockListRef): BlockListRef | null => {
      if (getListChildrenIds(listRef).includes(childId)) return listRef;
      for (const nestedId of getListChildrenIds(listRef)) {
        const nestedBlock = document[nestedId];
        if (!nestedBlock) continue;
        const nestedListRefs: BlockListRef[] =
          nestedBlock.type === 'Container'
            ? [{ kind: 'container', ownerBlockId: nestedId }]
            : nestedBlock.type === 'ColumnsContainer'
              ? [0, 1, 2].map((columnIndex) => ({
                  kind: 'column',
                  ownerBlockId: nestedId,
                  columnIndex: columnIndex as 0 | 1 | 2,
                }))
              : [];
        for (const nestedListRef of nestedListRefs) {
          const result = visit(nestedId, nestedListRef);
          if (result) return result;
        }
      }
      void ownerBlockId;
      return null;
    };
    return visit(rootBlockId, rootListRef);
  };

  const replaceWithSavedBlock = (temporaryBlockId: string, savedBlock: TReaderDocument) => {
    const savedRoot = savedBlock.root;
    if (!savedRoot || savedRoot.type !== 'EmailLayout') return;
    const savedRootChildren = savedRoot.data.childrenIds ?? [];
    if (savedRootChildren.length === 0) return;

    const nextDocument = { ...document };
    const idMap = new Map<string, string>();
    const createId = () => {
      let id = '';
      do id = `block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      while (nextDocument[id] || idMap.has(id));
      return id;
    };
    const copyTree = (oldId: string): string => {
      const block = savedBlock[oldId];
      const newId = createId();
      idMap.set(oldId, newId);
      const clonedBlock = structuredClone(block);
      nextDocument[newId] = clonedBlock;
      getBlockChildrenIdsFromDocument(savedBlock, oldId).forEach(copyTree);
      return newId;
    };
    const getBlockChildrenIdsFromDocument = (
      source: TReaderDocument,
      blockId: string,
    ): string[] => {
      const block = source[blockId];
      if (!block) return [];
      if (block.type === 'Container') return block.data?.props?.childrenIds ?? [];
      if (block.type === 'ColumnsContainer')
        return (block.data?.props?.columns ?? []).flatMap((column) => column.childrenIds);
      return [];
    };
    const rootChildrenIds = savedRootChildren.map(copyTree);
    for (const [oldId, newId] of idMap) {
      const block = nextDocument[newId];
      if (!block) continue;
      if (block.type === 'Container') {
        block.data.props = {
          ...block.data.props,
          childrenIds: block.data.props?.childrenIds?.map((id) => idMap.get(id) ?? id),
        };
      } else if (block.type === 'ColumnsContainer') {
        block.data.props = {
          ...block.data.props,
          columns: block.data.props?.columns?.map((column) => ({
            ...column,
            childrenIds: column.childrenIds.map((id) => idMap.get(id) ?? id),
          })) as ColumnsChildrenTuple,
        };
      }
      void oldId;
    }
    const parentListRef = findParentList(temporaryBlockId);
    if (!parentListRef) return;
    const parentChildren = [...getListChildrenIds(parentListRef)];
    const index = parentChildren.indexOf(temporaryBlockId);
    if (index < 0) return;
    parentChildren.splice(index, 1, ...rootChildrenIds);
    setListChildrenIds(nextDocument, parentListRef, parentChildren);
    delete nextDocument[temporaryBlockId];
    applyDocumentChange(nextDocument);
    updateBlockInsertionIds((ids) => {
      const next = new Set(ids);
      next.delete(temporaryBlockId);
      return next;
    });
    setSelectedBlockId(rootChildrenIds[0] ?? null);
  };

  const renderBlock = (blockId: string, parentListRef: BlockListRef): JSX.Element | null => {
    const block = document[blockId];
    if (!block) {
      return null;
    }

    switch (block.type) {
      case 'EmailLayout': {
        const layoutData = block.data as EmailLayoutData;
        const childIds = layoutData.childrenIds ?? [];
        const layoutListRef = rootListRef;

        return (
          <div
            style={{
              backgroundColor: layoutData.backdropColor ?? 'transparent',
              color: layoutData.textColor ?? EMAIL_COLOR.primary,
              fontFamily: getFontFamily(layoutData.fontFamily),
              fontSize: '16px',
              fontWeight: '400',
              letterSpacing: '0.15008px',
              lineHeight: '1.5',
              margin: '0',
              minHeight: '100%',
              padding: '16px 0',
              width: '100%',
            }}
            onClick={(event) => {
              const target = event.target as HTMLElement | null;

              if (target?.closest('[data-block-wrapper]')) {
                return;
              }

              setSelectedBlockId(null);
            }}
          >
            <table
              align="center"
              width="100%"
              style={{
                backgroundColor: layoutData.canvasColor ?? EMAIL_COLOR.primaryInverted,
                ...getBorderStyles(layoutData),
                borderCollapse: 'separate',
                borderRadius: layoutData.borderRadius ?? undefined,
                borderSpacing: 0,
                margin: '0 auto',
                maxWidth: '600px',
                overflow: (layoutData.borderRadius ?? 0) > 0 ? 'hidden' : undefined,
              }}
              role="presentation"
              cellSpacing="0"
              cellPadding="0"
              border={0}
            >
              <tbody>
                <tr style={{ width: '100%' }}>
                  <td>
                    <BlockChildrenList
                      listRef={layoutListRef}
                      childIds={childIds}
                      className="flex flex-col"
                      isDropDisabled={isBlock}
                    >
                      {childIds.length === 0 ? (
                        <div className="flex min-h-24 items-center justify-center py-8">
                          <BlockMenu
                            position="bottom"
                            onSelectBlockType={(nextBlockType) =>
                              insertBlockIntoList(layoutListRef, nextBlockType)
                            }
                          />
                        </div>
                      ) : (
                        childIds.map((childId) => (
                          <Fragment key={childId}>{renderBlock(childId, layoutListRef)}</Fragment>
                        ))
                      )}
                    </BlockChildrenList>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      }

      case 'Container': {
        const containerData = block.data as ContainerData;
        const isSelected = selectedBlockId === blockId;
        const isRootContainer = isBlock && parentListRef.kind === 'root';
        const childrenIds = containerData.props?.childrenIds ?? [];
        const containerListRef: BlockListRef = { kind: 'container', ownerBlockId: blockId };

        return (
          <BlockWrapper
            blockId={blockId}
            blockType="container"
            existingBlockNames={savedBlocks.map((block) => block.name)}
            isSelected={isSelected}
            onDelete={() => deleteBlock(blockId)}
            isStructureLocked={isRootContainer}
            onDuplicate={
              isRootContainer || blockInsertionIds.has(blockId)
                ? undefined
                : () => duplicateBlock(blockId, parentListRef)
            }
            onInsertBlock={(position, nextBlockType) =>
              insertBlockRelativeTo(blockId, parentListRef, position, nextBlockType)
            }
            onSaveBlock={
              onSaveBlock && !blockInsertionIds.has(blockId) && childrenIds.length > 0
                ? async (name, description) => {
                    const savedBlockDocument = getSavedBlockDocument(blockId);
                    await onSaveBlock({
                      name,
                      description,
                      document: savedBlockDocument,
                      html: renderToStaticMarkup(savedBlockDocument, { rootBlockId: 'root' }),
                    });
                  }
                : undefined
            }
            parentListId={getBlockListId(parentListRef)}
            onSelect={() => setSelectedBlockId(blockId)}
          >
            <BlockChildrenList
              listRef={containerListRef}
              childIds={childrenIds}
              className="flex flex-col"
            >
              <Container style={containerData.style}>
                {blockInsertionIds.has(blockId) ? (
                  <EmptyBlockArea>
                    <div className="px-8">
                      <p className="text-right text-sm text-muted-foreground">
                        Selecciona un bloque desde el panel de configuración{' '}
                        <span aria-label="emoji dedo señalando a la derecha" role="img">
                          👉
                        </span>
                      </p>
                    </div>
                  </EmptyBlockArea>
                ) : childrenIds.length === 0 ? (
                  <EmptyBlockArea>
                    <BlockMenu
                      position="bottom"
                      onSelectBlockType={(nextBlockType) =>
                        insertBlockIntoList(containerListRef, nextBlockType)
                      }
                    />
                  </EmptyBlockArea>
                ) : (
                  childrenIds.map((childId) => (
                    <Fragment key={childId}>{renderBlock(childId, containerListRef)}</Fragment>
                  ))
                )}
              </Container>
            </BlockChildrenList>
          </BlockWrapper>
        );
      }

      case 'ColumnsContainer': {
        const columnsData = block.data as ColumnsContainerData;
        const isSelected = selectedBlockId === blockId;
        const columns = columnsData.props?.columns ?? [
          { childrenIds: [] },
          { childrenIds: [] },
          { childrenIds: [] },
        ];
        const columnListRefs: [BlockListRef, BlockListRef, BlockListRef] = [
          { kind: 'column', ownerBlockId: blockId, columnIndex: 0 },
          { kind: 'column', ownerBlockId: blockId, columnIndex: 1 },
          { kind: 'column', ownerBlockId: blockId, columnIndex: 2 },
        ];

        return (
          <BlockWrapper
            blockId={blockId}
            blockType="column"
            isSelected={isSelected}
            onDelete={() => deleteBlock(blockId)}
            onDuplicate={() => duplicateBlock(blockId, parentListRef)}
            onInsertBlock={(position, nextBlockType) =>
              insertBlockRelativeTo(blockId, parentListRef, position, nextBlockType)
            }
            parentListId={getBlockListId(parentListRef)}
            onSelect={() => setSelectedBlockId(blockId)}
          >
            <ColumnsContainer
              style={columnsData.style}
              props={{
                fixedWidths: columnsData.props?.fixedWidths,
                columnsCount: columnsData.props?.columnsCount,
                columnsGap: columnsData.props?.columnsGap,
                contentAlignment: columnsData.props?.contentAlignment,
              }}
              columns={columns.map((column, columnIndex) => {
                const columnListRef = columnListRefs[columnIndex];

                return (
                  <BlockChildrenList
                    key={`${blockId}-${columnIndex}`}
                    listRef={columnListRef}
                    childIds={column.childrenIds}
                    className={cn(
                      getColumnListClassName(columnsData.props?.contentAlignment),
                      column.childrenIds.length === 0 && 'min-h-24',
                    )}
                  >
                    {column.childrenIds.length === 0 ? (
                      <EmptyBlockArea className="h-full">
                        <BlockMenu
                          position="bottom"
                          onSelectBlockType={(nextBlockType) =>
                            insertBlockIntoList(columnListRef, nextBlockType)
                          }
                        />
                      </EmptyBlockArea>
                    ) : (
                      column.childrenIds.map((childId) => (
                        <Fragment key={childId}>{renderBlock(childId, columnListRef)}</Fragment>
                      ))
                    )}
                  </BlockChildrenList>
                );
              })}
            />
          </BlockWrapper>
        );
      }

      case 'Heading': {
        const headingData = block.data as HeadingData;
        const isSelected = selectedBlockId === blockId;

        return (
          <BlockWrapper
            blockId={blockId}
            blockType="header"
            isSelected={isSelected}
            onDelete={() => deleteBlock(blockId)}
            onDuplicate={() => duplicateBlock(blockId, parentListRef)}
            onInsertBlock={(position, nextBlockType) =>
              insertBlockRelativeTo(blockId, parentListRef, position, nextBlockType)
            }
            parentListId={getBlockListId(parentListRef)}
            onSelect={() => setSelectedBlockId(blockId)}
          >
            <Heading style={headingData.style} props={headingData.props} />
          </BlockWrapper>
        );
      }

      case 'Text': {
        const textData = block.data as TextData;
        const isSelected = selectedBlockId === blockId;

        return (
          <BlockWrapper
            blockId={blockId}
            blockType="text"
            isSelected={isSelected}
            onDelete={() => deleteBlock(blockId)}
            onDuplicate={() => duplicateBlock(blockId, parentListRef)}
            onInsertBlock={(position, nextBlockType) =>
              insertBlockRelativeTo(blockId, parentListRef, position, nextBlockType)
            }
            parentListId={getBlockListId(parentListRef)}
            onSelect={() => setSelectedBlockId(blockId)}
          >
            <Text {...textData} />
          </BlockWrapper>
        );
      }

      case 'Button': {
        const buttonData = block.data as ButtonData;
        const isSelected = selectedBlockId === blockId;

        return (
          <BlockWrapper
            blockId={blockId}
            blockType="button"
            isSelected={isSelected}
            onDelete={() => deleteBlock(blockId)}
            onDuplicate={() => duplicateBlock(blockId, parentListRef)}
            onInsertBlock={(position, nextBlockType) =>
              insertBlockRelativeTo(blockId, parentListRef, position, nextBlockType)
            }
            parentListId={getBlockListId(parentListRef)}
            onSelect={() => setSelectedBlockId(blockId)}
          >
            <EmailButton {...buttonData} />
          </BlockWrapper>
        );
      }

      case 'Divider': {
        const dividerData = block.data as DividerData;
        const isSelected = selectedBlockId === blockId;

        return (
          <BlockWrapper
            blockId={blockId}
            blockType="divider"
            isSelected={isSelected}
            onDelete={() => deleteBlock(blockId)}
            onDuplicate={() => duplicateBlock(blockId, parentListRef)}
            onInsertBlock={(position, nextBlockType) =>
              insertBlockRelativeTo(blockId, parentListRef, position, nextBlockType)
            }
            parentListId={getBlockListId(parentListRef)}
            onSelect={() => setSelectedBlockId(blockId)}
          >
            <Divider {...dividerData} />
          </BlockWrapper>
        );
      }

      case 'Html': {
        const htmlData = block.data as HtmlData;
        const isSelected = selectedBlockId === blockId;

        return (
          <BlockWrapper
            blockId={blockId}
            blockType="html"
            isSelected={isSelected}
            onDelete={() => deleteBlock(blockId)}
            onDuplicate={() => duplicateBlock(blockId, parentListRef)}
            onInsertBlock={(position, nextBlockType) =>
              insertBlockRelativeTo(blockId, parentListRef, position, nextBlockType)
            }
            parentListId={getBlockListId(parentListRef)}
            onSelect={() => setSelectedBlockId(blockId)}
          >
            <Html {...htmlData} />
          </BlockWrapper>
        );
      }

      case 'Image': {
        const imageData = block.data as ImageData;
        const isSelected = selectedBlockId === blockId;

        return (
          <BlockWrapper
            blockId={blockId}
            blockType="image"
            isSelected={isSelected}
            onDelete={() => deleteBlock(blockId)}
            onDuplicate={() => duplicateBlock(blockId, parentListRef)}
            onInsertBlock={(position, nextBlockType) =>
              insertBlockRelativeTo(blockId, parentListRef, position, nextBlockType)
            }
            parentListId={getBlockListId(parentListRef)}
            onSelect={() => setSelectedBlockId(blockId)}
          >
            <Image {...imageData} />
          </BlockWrapper>
        );
      }

      case 'Spacer': {
        const spacerData = block.data as SpacerData;
        const isSelected = selectedBlockId === blockId;

        return (
          <BlockWrapper
            blockId={blockId}
            blockType="space"
            isSelected={isSelected}
            onDelete={() => deleteBlock(blockId)}
            onDuplicate={() => duplicateBlock(blockId, parentListRef)}
            onInsertBlock={(position, nextBlockType) =>
              insertBlockRelativeTo(blockId, parentListRef, position, nextBlockType)
            }
            parentListId={getBlockListId(parentListRef)}
            onSelect={() => setSelectedBlockId(blockId)}
          >
            <Spacer {...spacerData} />
          </BlockWrapper>
        );
      }

      case 'Avatar': {
        const avatarData = block.data as AvatarData;
        const isSelected = selectedBlockId === blockId;

        return (
          <BlockWrapper
            blockId={blockId}
            blockType="avatar"
            isSelected={isSelected}
            onDelete={() => deleteBlock(blockId)}
            onDuplicate={() => duplicateBlock(blockId, parentListRef)}
            onInsertBlock={(position, nextBlockType) =>
              insertBlockRelativeTo(blockId, parentListRef, position, nextBlockType)
            }
            parentListId={getBlockListId(parentListRef)}
            onSelect={() => setSelectedBlockId(blockId)}
          >
            <Avatar {...avatarData} />
          </BlockWrapper>
        );
      }

      default:
        return null;
    }
  };

  const undoRedoControls = (
    <div className="flex gap-2">
      <div className="flex gap-2">
        <Button
          aria-label="Deshacer"
          disabled={!canUndo}
          size="icon"
          title="Deshacer"
          variant="secondary"
          onClick={() => {
            // setSelectedBlockId(null);
            undo();
          }}
        >
          <Undo2Icon />
        </Button>
        <Button
          aria-label="Rehacer"
          disabled={!canRedo}
          size="icon"
          title="Rehacer"
          variant="secondary"
          onClick={() => {
            // setSelectedBlockId(null);
            redo();
          }}
        >
          <Redo2Icon />
        </Button>
      </div>
      <div
        aria-label="Tipo de vista previa"
        className="flex h-9 items-center gap-1 rounded-full bg-secondary p-1"
        role="group"
      >
        <Button
          aria-label="Escritorio"
          aria-pressed={previewMode === 'desktop'}
          className="size-7"
          size="icon-sm"
          title="Escritorio"
          variant={previewMode === 'desktop' ? 'default' : 'ghost'}
          onClick={() => setPreviewMode('desktop')}
        >
          <MonitorIcon />
        </Button>
        <Button
          aria-label="Móvil"
          aria-pressed={previewMode === 'mobile'}
          className="size-7"
          size="icon-sm"
          title="Móvil"
          variant={previewMode === 'mobile' ? 'default' : 'ghost'}
          onClick={() => setPreviewMode('mobile')}
        >
          <SmartphoneIcon />
        </Button>
      </div>
      <Button
        aria-label="Vista previa"
        aria-pressed={isPreviewing}
        className={cn({
          'ring-2 ring-warning ring-offset-2': isPreviewing,
        })}
        size="icon"
        title="Vista previa"
        variant={isPreviewing ? 'accent' : 'secondary'}
        onClick={() => {
          setSelectedBlockId(null);
          setIsPreviewing((previewing) => !previewing);
        }}
      >
        <ViewIcon />
      </Button>
      <Button
        aria-label="Descargar HTML"
        size="icon"
        title="Descargar HTML"
        variant="secondary"
        onClick={() => {
          setSelectedBlockId(null);
          const html = renderToStaticMarkup(document, { rootBlockId });
          const url = URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
          const link = window.document.createElement('a');

          link.href = url;
          link.download = getEmailDownloadFilename();
          link.click();
          URL.revokeObjectURL(url);
        }}
      >
        <DownloadIcon />
      </Button>
    </div>
  );

  return (
    <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragEnd={handleDragEnd}>
      <EditorEmailLayout
        treeContent={
          <ElementTree
            document={document}
            rootBlockId={rootBlockId}
            selectedBlockId={selectedBlockId}
            onSelect={(blockId) => {
              setIsPreviewing(false);
              setSelectedBlockId(blockId);
            }}
          />
        }
        selectedBlockId={selectedBlockId}
        isGlobalStylesSelected={!!selectedEmailLayoutData}
        onGlobalStylesClick={
          rootBlock?.type === 'EmailLayout' ? () => setSelectedBlockId(rootBlockId) : undefined
        }
        onSidebarToggle={() => setSelectedBlockId(null)}
        toolbarContent={undoRedoControls}
        mainContent={
          <div className="p-1">
            <div
              className={cn('flex w-full justify-center', {
                'bg-secondary/50 bg-[radial-gradient(circle,rgba(100,116,139,0.24)_1px,transparent_1px)] bg-size-[16px_16px]':
                  previewMode === 'mobile',
                'ring-2 ring-warning ring-offset-2': isPreviewing,
              })}
              onClick={(event) => {
                if (event.target !== event.currentTarget) {
                  return;
                }

                setSelectedBlockId(null);
              }}
            >
              <div
                className={cn('w-full transition-all duration-300 ease-in-out', {
                  'max-w-105': previewMode === 'mobile',
                  'max-w-none': previewMode === 'desktop',
                })}
              >
                {isPreviewing ? (
                  <Reader document={document} rootBlockId={rootBlockId} />
                ) : (
                  renderBlock(rootBlockId, rootListRef)
                )}
              </div>
            </div>
          </div>
        }
        editorContent={
          <div className="space-y-3">
            {selectedEmailLayoutData ? (
              <GlobalStylesEditorContent
                data={selectedEmailLayoutData}
                onChange={(updates) =>
                  applyDocumentChange({
                    ...document,
                    [rootBlockId]: {
                      type: 'EmailLayout',
                      data: { ...selectedEmailLayoutData, ...updates },
                    },
                  })
                }
              />
            ) : headingEditorProps ? (
              <HeadingEditorContent {...headingEditorProps} />
            ) : textEditorProps ? (
              <TextEditorContent {...textEditorProps} />
            ) : buttonEditorProps ? (
              <ButtonEditorContent {...buttonEditorProps} />
            ) : imageEditorProps ? (
              <ImageEditorContent {...imageEditorProps} />
            ) : avatarEditorProps ? (
              <AvatarEditorContent {...avatarEditorProps} />
            ) : dividerEditorProps ? (
              <DividerEditorContent {...dividerEditorProps} />
            ) : spacerEditorProps ? (
              <SpacerEditorContent {...spacerEditorProps} />
            ) : htmlEditorProps ? (
              <HtmlEditorContent {...htmlEditorProps} />
            ) : columnsEditorProps ? (
              <ColumnsEditorContent {...columnsEditorProps} />
            ) : containerEditorProps ? (
              <ContainerEditorContent {...containerEditorProps} />
            ) : null}
          </div>
        }
        emptyContent={
          <div className="space-y-3">
            <div className="rounded-lg border p-3 text-sm">
              <p className="text-muted-foreground">Haz clic sobre un elemento para editarlo</p>
            </div>
          </div>
        }
      />
    </DndContext>
  );
}
